import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { DeadlineContract, DeadlineContractInput, DeadlineContractInterval } from '@core/interfaces/deadlines-contracts';
import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface ToTimeParams {
  time?: number | string;
  valueInMinutes?: number | string;
  unity: 'minutes' | 'hours' | 'days';
}

interface Interval {
  consider: boolean;
  start: string;
  end: string;
}

export class DeadlinesContractsMapper {
  public static readonly daysOfWeek = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  private static readonly timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  static toWeekDay(digitOfDay: number): string {
    return this.daysOfWeek[digitOfDay];
  }

  static toTime({ valueInMinutes, time, unity }: ToTimeParams): number {
    const sanitizedTime = Number(time || valueInMinutes);
    const isConversionFromMinutes = !!valueInMinutes;

    if (!sanitizedTime || isNaN(sanitizedTime)) {
      return 0;
    }

    if (isConversionFromMinutes && unity === 'hours') {
      return sanitizedTime / 60;
    }

    if (!isConversionFromMinutes && unity === 'hours') {
      return sanitizedTime * 60;
    }

    if (isConversionFromMinutes && unity === 'days') {
      return sanitizedTime / 1440;
    }

    if (!isConversionFromMinutes && unity === 'days') {
      return sanitizedTime * 1440;
    }

    return sanitizedTime;
  }

  static toTimeWithTimezone(value?: string): string {
    if (!value) {
      return null;
    }

    const utcTime = new Date(`1970-01-01T${value}Z`);
    return formatInTimeZone(utcTime, this.timezone, 'HH:mm');
  }

  static toCustomIntervalInput(intervals: Interval[]): DeadlineContractInterval {
    if (!intervals || !intervals.length) {
      return null;
    }

    const customInterval = {};
    for (const [index, interval] of intervals.entries()) {
      if (interval.consider) {
        const day = this.toWeekDay(index);
        customInterval[day] = {
          start: format(parse(interval.start, 'HH:mm:ss', new Date()), 'HH:mm'),
          end: format(parse(interval.end, 'HH:mm:ss', new Date()), 'HH:mm')
        };
      }
    }

    return customInterval;
  }

  static toCustomIntervalForm(customInterval: Partial<DeadlineContractInterval>, formBuilder: FormBuilder): FormArray {
    const controls = [];
    for (let digitOfDay = 0; digitOfDay < 7; digitOfDay++) {
      const weekDay = DeadlinesContractsMapper.toWeekDay(digitOfDay);
      const dayInterval = customInterval && customInterval[weekDay];
      const startValue = dayInterval ? this.toTimeWithTimezone(dayInterval.start) : null;
      const endValue = dayInterval ? this.toTimeWithTimezone(dayInterval.end) : null;

      const dayGroup = formBuilder.group({
        consider: [Boolean(dayInterval), [Validators.required]],
        start: [{ disabled: Boolean(dayInterval), value: startValue }],
        end: [{ disabled: Boolean(dayInterval), value: endValue }]
      });

      controls.push(dayGroup);
    }

    return formBuilder.array(controls);
  }

  static toDeadlineContractInput(formGroupValue: Record<string, any>): DeadlineContractInput {
    return {
      name: formGroupValue.name,
      description: formGroupValue.description,
      considerHoliday: formGroupValue.considerHoliday,
      customIntervalTimezone: formGroupValue.customInterval && formGroupValue.customInterval.length && this.timezone,
      customInterval: this.toCustomIntervalInput(formGroupValue.customInterval),
      deadlineViolationReasonIds: (formGroupValue.violationReasons || []).map(({ id }) => id),
      responseRisk: {
        valueInMinutes: this.toTime(formGroupValue.responseRisk),
        unity: formGroupValue.responseRisk.unity
      },
      responseDeadline: {
        valueInMinutes: this.toTime(formGroupValue.responseDeadline),
        unity: formGroupValue.responseDeadline.unity
      },
      solutionRisk: {
        valueInMinutes: this.toTime(formGroupValue.solutionRisk),
        unity: formGroupValue.solutionRisk.unity
      },
      solutionDeadline: {
        valueInMinutes: this.toTime(formGroupValue.solutionDeadline),
        unity: formGroupValue.solutionDeadline.unity
      }
    };
  }

  static toDeadlineContractForm(deadlineContract: DeadlineContract): Record<string, any> {
    return {
      name: deadlineContract.name,
      description: deadlineContract.description,
      considerHoliday: deadlineContract.considerHoliday,
      hasCustomInterval: deadlineContract.hasCustomInterval,
      violationReasons: deadlineContract.deadlineViolationReasons && deadlineContract.deadlineViolationReasons.length
        ? deadlineContract.deadlineViolationReasons
        : null,
      responseRisk: {
        time: this.toTime(deadlineContract.responseRisk),
        unity: deadlineContract.responseRisk.unity,
      },
      solutionRisk: {
        time: this.toTime(deadlineContract.solutionRisk),
        unity: deadlineContract.solutionRisk.unity,
      },
      responseDeadline: {
        time: this.toTime(deadlineContract.responseDeadline),
        unity: deadlineContract.responseDeadline.unity,
      },
      solutionDeadline: {
        time: this.toTime(deadlineContract.solutionDeadline),
        unity: deadlineContract.solutionDeadline.unity,
      }
    };
  }
}
